import React, { useEffect, useMemo } from 'react'
import dayjs from 'dayjs'
import { FormProvider, useForm } from 'react-hook-form'
import { useOrientation } from '@uidotdev/usehooks'
import { useLocation } from 'react-router-dom'
import clsx from 'clsx'

import { Box } from 'components/layout/box'
import { Card } from 'components/Card'
import { Leaderboard } from 'components/Leaderboard'
import { useTabsContext } from 'components/Tabs/TabsProvider'
import { useArenaContext } from 'context/ArenaContext'

import { LatestTrades } from './LatestTrades/LatestTrades'
import { BotFilter } from '../components/BotFilters/BotFilter'
import * as styles from './Arena.css'

interface FormValues {
	selectedStrategies: string[]
	selectedTickers: string[]
	selectedBotIds: number[]
}

interface Props {
	isLatestTradesLoading: boolean
	onFirstLoad?: (cachedComponent: React.ReactNode) => void
}

export const Arena = ({ isLatestTradesLoading, onFirstLoad }: Props) => {
	const { onActiveTabChange } = useTabsContext()
	const { arenaBotsData, setArenaSelectedBot, setArenaSelectedBotName } = useArenaContext()
	const location = useLocation()

	const { type: orientationType } = useOrientation()

	// Extract unique strategies, tickers, and bots
	const strategies = useMemo(() => {
		const strategyMap = new Map<string, { label: string; value: string; isAvailable: boolean }>()
		arenaBotsData.forEach(bot => {
			if (bot.strategy) {
				// Here we ensure only one entry per strategy
				strategyMap.set(bot.strategy, {
					label: bot.strategy,
					value: bot.strategy,
					isAvailable: !!bot.trades && bot.trades.length > 0
				})
			}
		})
		return Array.from(strategyMap.values())
	}, [arenaBotsData])

	const tickers = useMemo(() => {
		const tickerMap = new Map<string, { label: string; value: string; isAvailable: boolean }>()
		arenaBotsData.forEach(bot => {
			if (bot.trades && bot.trades.length > 0) {
				bot.trades.forEach(ticker => {
					// Using a map ensures unique tickers with defined availability
					tickerMap.set(ticker, {
						label: ticker,
						value: ticker,
						isAvailable: !!bot.trades && bot.trades.length > 0
					})
				})
			}
		})
		return Array.from(tickerMap.values())
	}, [arenaBotsData])

	const botOptions = useMemo(() => {
		// Separate portfolio and trader bots
		const portfolioBots = arenaBotsData
			.filter(bot => bot.role === 'portfolio')
			.map(bot => ({
				label: bot.bot_name,
				value: bot.bot_id,
				avatar: bot.avatar,
				isAvailable: bot.trades && bot.trades.length > 0,
				role: 'portfolio' as const,
				isSeparator: false
			}))

		const traderBots = arenaBotsData
			.filter(bot => bot.role === 'trader')
			.map(bot => ({
				label: bot.bot_name,
				value: bot.bot_id,
				avatar: bot.avatar,
				isAvailable: bot.trades && bot.trades.length > 0,
				role: 'trader' as const,
				isSeparator: false
			}))

		// Add a separator between portfolio and trader bots
		const separator = {
			label: '──────────',
			value: -1, // Using -1 as a special value for separator
			avatar: '',
			isAvailable: false,
			isSeparator: true
		}

		// Combine them with portfolio bots first, then separator, then trader bots
		return [...portfolioBots, separator, ...traderBots]
	}, [arenaBotsData])

	// Set up form methods
	const methods = useForm<FormValues>({
		defaultValues: {
			selectedStrategies: [],
			selectedTickers: [],
			selectedBotIds: []
		}
	})

	const { watch, reset } = methods
	const selectedStrategies = watch('selectedStrategies') || []
	const selectedTickers = watch('selectedTickers') || []
	const selectedBotIds = watch('selectedBotIds') || []

	useEffect(() => {
		if (strategies.length > 0 || tickers.length > 0 || botOptions.length > 0) {
			reset({
				selectedStrategies: strategies.map(s => s.value),
				selectedTickers: tickers.map(t => t.value),
				selectedBotIds: botOptions.filter(option => !option.isSeparator).map(option => option.value) // Exclude separator
			})
		}
	}, [strategies, tickers, botOptions, reset])

	const filteredBots = useMemo(() => {
		return arenaBotsData
			.filter(bot => {
				const strategyMatch = selectedStrategies.includes(bot.strategy)
				const tickerMatch = bot.trades.length === 0 || bot.trades.some(ticker => selectedTickers.includes(ticker))
				const idMatch = selectedBotIds.includes(bot.bot_id)

				return strategyMatch && tickerMatch && idMatch
			})
			.sort((a, b) => {
				// Get the most recent valid trade date for each bot
				const getLatestValidDate = (bot: any) => {
					if (!bot.trades || !Array.isArray(bot.trades)) return null
					const validTrades = bot.trades.filter((trade: { datetime: string }) => dayjs(trade.datetime).isValid())
					if (validTrades.length === 0) return null
					return Math.max(...validTrades.map((trade: { datetime: string }) => dayjs(trade.datetime).valueOf()))
				}

				const latestDateA = getLatestValidDate(a)
				const latestDateB = getLatestValidDate(b)

				// If both have no valid dates, maintain original order
				if (!latestDateA && !latestDateB) return 0
				// If only A has no valid date, move it to the end
				if (!latestDateA) return 1
				// If only B has no valid date, move it to the end
				if (!latestDateB) return -1
				// Both have valid dates, sort by most recent first
				return latestDateB - latestDateA
			})
	}, [arenaBotsData, selectedStrategies, selectedTickers, selectedBotIds])

	const handleAvatarClick = (botId: number) => {
		const arenaBot = arenaBotsData.find(bot => bot.bot_id === botId)
		if (!arenaBot) {
			return
		}
		setArenaSelectedBot(arenaBot)
		setArenaSelectedBotName(arenaBot.bot_name.toLowerCase())
		onActiveTabChange('Roster')
	}

	useEffect(() => {
		if (location.hash) {
			const element = document.querySelector(location.hash)

			if (element) {
				element.scrollIntoView({ behavior: 'smooth' })
			}
		}
	}, [location])

	// Add this effect to cache the component after it's loaded
	useEffect(() => {
		// Only cache the component once all data is loaded
		if (!isLatestTradesLoading && filteredBots.length > 0 && onFirstLoad) {
			// Create a shallow clone of the component to cache
			const cachedComponent = (
				<Box
					className={clsx({
						[styles.portraitLayout]: orientationType === 'portrait-primary',
						[styles.layout]: orientationType !== 'portrait-primary'
					})}>
					<FormProvider {...methods}>
						<Card
							title="Latest Trades"
							headerChildren={
								<Box display="flex" gap={2}>
									<BotFilter<'selectedBotIds'>
										title="Bots"
										options={botOptions}
										field="selectedBotIds"
										showRoleColors
									/>
									<BotFilter<'selectedStrategies'> title="Strategy" options={strategies} field="selectedStrategies" />
									<BotFilter<'selectedTickers'>
										title="Ticker"
										options={tickers}
										field="selectedTickers"
										hasDesktopSearch
									/>
								</Box>
							}
							style={{ gridArea: 'latestTrades' }}>
							<LatestTrades traders={filteredBots} />
						</Card>
					</FormProvider>

					<Card title="Leaderboard" id="leaderboard" style={{ paddingBottom: '24px' }}>
						<Leaderboard isTransparent handleClick={handleAvatarClick} isLastUpdatedVisible />
					</Card>
				</Box>
			)

			onFirstLoad(cachedComponent)
		}
	}, [isLatestTradesLoading, filteredBots, onFirstLoad])

	return (
		// <Box className={styles.arenaContainer}>
		<Box
			className={clsx({
				[styles.portraitLayout]: orientationType === 'portrait-primary',
				[styles.layout]: orientationType !== 'portrait-primary'
			})}>
			<FormProvider {...methods}>
				<Card
					title="Latest Trades"
					isLoading={isLatestTradesLoading}
					hasSkeletonLoading={true}
					headerChildren={
						<Box display="flex" gap={2}>
							<BotFilter<'selectedBotIds'> title="Bots" options={botOptions} field="selectedBotIds" showRoleColors />
							<BotFilter<'selectedStrategies'> title="Strategy" options={strategies} field="selectedStrategies" />
							<BotFilter<'selectedTickers'> title="Ticker" options={tickers} field="selectedTickers" hasDesktopSearch />
						</Box>
					}
					style={{ gridArea: 'latestTrades' }}>
					<LatestTrades traders={filteredBots} isLoading={isLatestTradesLoading} />
				</Card>
			</FormProvider>

			<Card title="Leaderboard" id="leaderboard" style={{ paddingBottom: '24px' }}>
				<Leaderboard isTransparent handleClick={handleAvatarClick} isLastUpdatedVisible />
			</Card>
		</Box>
		// </Box>
	)
}
